/**
 * useCheckInInterrupt — lane LC. The one queue behind "Before you check in",
 * shared by every check-in surface (the `useArrivalVerification` shape).
 *
 * A surface builds the plan for a booking (or a household) and, when
 * `plan.needed`, calls `ask(...)` instead of POSTing; requests are answered
 * ONE AT A TIME, in order. The hook owns the dialog element; the surface
 * renders `dialog` once, anywhere in its tree.
 */
import { useCallback, useState } from 'react';
import { BeforeCheckInDialog, type CheckInInterruptRequest } from './BeforeCheckInDialog.js';

export interface CheckInInterrupt {
  /** Queue a step. Duplicate keys collapse onto the first. */
  ask: (request: CheckInInterruptRequest) => void;
  /** True while a step is on screen. */
  pending: boolean;
  /** The dialog for the request at the head of the queue, or null. */
  dialog: JSX.Element | null;
}

export function useCheckInInterrupt(): CheckInInterrupt {
  const [queue, setQueue] = useState<CheckInInterruptRequest[]>([]);

  const ask = useCallback((request: CheckInInterruptRequest) => {
    setQueue((curr) => (curr.some((r) => r.key === request.key) ? curr : [...curr, request]));
  }, []);

  const shift = useCallback(() => {
    setQueue((curr) => curr.slice(1));
  }, []);

  const head = queue[0] ?? null;
  const dialog = head ? (
    <BeforeCheckInDialog
      key={head.key}
      request={head}
      onProceed={(outcome) => {
        shift();
        void head.onProceed(outcome);
      }}
      onCancel={() => {
        shift();
        head.onCancel?.();
      }}
    />
  ) : null;

  return { ask, pending: head !== null, dialog };
}
